import React, { useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import LoadingScreen from './common/LoadingScreen';
import { flwOrchestratorApi } from '../services';
import { AppDispatch } from '@/store';
import { msalConfig } from '@/auth/authConfig';
import { ROUTES } from '@/constants/routes';
import { NavigationService } from '@/utils/navigationService';
import type { PolicyEvaluateResponse, TenantGeoRegionResponse } from '../models/flw-orchestrator.types';
import { Device, DevicePlatform, Environment, PolicyEvaluationStep } from '../models/flw-orchestrator.types';
import { PolicyEvaluationService, type PolicyEvaluationParams } from '@/utils/policyEvaluationService';
import { trackAuthRedirect, collectAuthStatus, collectTenantRegion, recordRecoverableError, logScreenRender } from '../telemetry/telemetryEvents';
import { configureLoggerForRegion, updateTelemetryTenant, updateTelemetryUserId } from '../telemetry/telemetry';
import { logDev as debugLog, errorDev } from '@/logging/devConsole';

/**
 * AuthRedirectHandler processes the authentication redirect, initializes ECS,
 * evaluates MFA/MAM policies, and navigates based on policy results.
 */
const TEAMS_APP_ID = "1fec8e78-bce4-4aaf-ab1b-5451cc387264";

// Track if we've started processing to prevent double execution in StrictMode
let isProcessingRedirect = false;

export default function AuthRedirectHandler() {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [hasProcessed, setHasProcessed] = React.useState(false);

  useEffect(() => {
    if (hasProcessed || isProcessingRedirect) {
      return;
    }
    isProcessingRedirect = true;

    trackAuthRedirect(false);

    let isMounted = true;

    const processRedirect = async () => {
      try {
        debugLog('AuthRedirectHandler: Starting processRedirect');
        const authResult = await instance.handleRedirectPromise();
        debugLog('AuthRedirectHandler: handleRedirectPromise result:', authResult);
        debugLog('AuthRedirectHandler: isMounted status:', isMounted);

        if (!isMounted) {
          debugLog('AuthRedirectHandler: Component unmounted, returning');
          return;
        }

        debugLog('AuthRedirectHandler: Proceeding after mount check');
        let selectedDevice: Device | null = null;
        let resumeEvaluate = false;
        const rawState = authResult?.state;
        debugLog('AuthRedirectHandler: rawState:', rawState);
        if (rawState) {
          try {
            const parsed = JSON.parse(rawState);
            selectedDevice = parsed.selectedDevice;
            resumeEvaluate = parsed.resumeEvaluate === true;
            debugLog('AuthRedirectHandler: Parsed device from state:', selectedDevice, 'resumeEvaluate:', resumeEvaluate);
          } catch (error) {
            debugLog('AuthRedirectHandler: Error parsing state:', error);
          }
        }

        // If no authResult but we have an active account, this might be a page refresh
        // or returning from claims challenge redirect - check for resumeEvaluate flag
        if (!authResult?.account) {
          const activeAccount = instance.getActiveAccount();
          const storedDevice = sessionStorage.getItem('selectedDevice') as Device | null;
          
          debugLog('AuthRedirectHandler: No authResult.account, checking activeAccount:', activeAccount?.username, 'storedDevice:', storedDevice);
          
          if (activeAccount && storedDevice) {
            // We have an active account from session - proceed with evaluate
            debugLog('AuthRedirectHandler: Using active account from session, proceeding with evaluate');
            selectedDevice = storedDevice;
            // Continue with the flow using the active account
          } else {
            debugLog('AuthRedirectHandler: No account found after redirect - authResult:', authResult);
            setHasProcessed(true);
            collectAuthStatus('unknown');
            navigate(ROUTES.APP_DOWNLOAD, { state: { selectedDevice: null } });
            return;
          }
        }

        // Use authResult account if available, otherwise fall back to active account
        const account = authResult?.account || instance.getActiveAccount();
        if (!account) {
          debugLog('AuthRedirectHandler: No account available');
          setHasProcessed(true);
          collectAuthStatus('unknown');
          navigate(ROUTES.APP_DOWNLOAD, { state: { selectedDevice: null } });
          return;
        }

        debugLog('AuthRedirectHandler: Account found:', account.username);
        instance.setActiveAccount(account);
        sessionStorage.setItem('msal.activeAccountId', account.localAccountId);
        if (selectedDevice) {
          sessionStorage.setItem('selectedDevice', selectedDevice);
        }
        const telemetryUserId = account.localAccountId || account.homeAccountId;
        updateTelemetryUserId(telemetryUserId);
        updateTelemetryTenant(account.tenantId);
        debugLog('AuthRedirectHandler: UserInfo.Id set on telemetry:', telemetryUserId);
        debugLog('AuthRedirectHandler: Session ID in storage:', sessionStorage.getItem('byodWizardSessionId'));

        // Log screen view AFTER user ID and tenant are set so both are attached to the event
        logScreenRender('AuthRedirectHandler');

        collectAuthStatus('set', false);

        const isValidDevice = selectedDevice === Device.ANDROID || selectedDevice === Device.IOS;
        if (!isValidDevice) {
          debugLog('AuthRedirectHandler: No valid device selected');
          setHasProcessed(true);
          navigate(ROUTES.APP_DOWNLOAD, { state: { selectedDevice: null } });
          return;
        }

        const getEnvironmentFromHost = (): Environment => {
          const hostname = window.location.hostname;
          if (hostname.includes('-int.') || hostname.startsWith('int.') || hostname === 'localhost') return Environment.INT;
          if (hostname.includes('-ppe.')) return Environment.PPE;
          return Environment.PROD;
        };
        const env: Environment = (import.meta.env.VITE_FLW_ENVIRONMENT as Environment | undefined) ?? getEnvironmentFromHost();
        let region: string;
        try {
          const action = await dispatch(flwOrchestratorApi.endpoints.getTenantGeoRegion.initiate(env));
          if (action.data) {
            const regionResponse = action.data as TenantGeoRegionResponse;
            region = regionResponse.region ?? '';
            if (!region) {
              throw new Error('Region missing from tenant geo response');
            }
          } else {
            throw new Error('Geo-region lookup failed');
          }
        } catch {
          region = env === Environment.INT ? 'AMER' : 'EMEA';
        }

        // Store region for feedback SDK
        sessionStorage.setItem('tenantRegion', region);

        const loggerEnvironment = configureLoggerForRegion(region);
        debugLog(`[EUDB] Tenant region: ${region}, Telemetry environment: ${loggerEnvironment}`);
        collectTenantRegion({
          region,
          environment: loggerEnvironment
        });

        if (!selectedDevice) {
          throw new Error('Device selection is required');
        }

        const policyParams: PolicyEvaluationParams = {
          env,
          userId: account.localAccountId,
          applicationId: TEAMS_APP_ID,
          devicePlatform: selectedDevice === Device.IOS ? DevicePlatform.IOS : DevicePlatform.ANDROID,
          clientAppType: 'mobileAppsAndDesktopClients',
          region,
          selectedDevice,
        };

        const result = await PolicyEvaluationService.evaluateAndNavigate(dispatch, navigate, policyParams);
        setHasProcessed(true);

        if (result.error) {
          errorDev('AuthRedirectHandler: Policy evaluation failed:', result.error);
          PolicyEvaluationService.navigateToRetry(
            navigate, 
            result.failedStep || PolicyEvaluationStep.MFA, 
            policyParams, 
            result.mfaResult,
            0,
            false,
            result.mamResult
          );
        }
      } catch (error) {
        errorDev('AuthRedirectHandler: Error handling redirect:', error);
        setHasProcessed(true);
        isProcessingRedirect = false;
        collectAuthStatus('unknown');
        recordRecoverableError('auth redirect failure', undefined, undefined, error);
        navigate(ROUTES.APP_DOWNLOAD, { state: { selectedDevice: null } });
      }
    };

    processRedirect();

    return () => {
      isMounted = false;
      isProcessingRedirect = false;
    };
  }, [instance, navigate, dispatch, hasProcessed]);

  return <LoadingScreen />;
}